import React from "react";
import type { MachineNameAndSourceId } from "@common/types/server-list-types";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import closeIcon from "@src/assets/svg/close-icon.svg";
import styles from "./multiple-text-values-entry-card.module.scss";

const cssClassName = "multiple-text-values-entry-card";

/** One entry of the editor's table. `shouldAutoFillLabel` mirrors the
 *  drawer's `shouldAutoPickUnit` pattern: true until the user types a label
 *  by hand, then false, so a later node change never clobbers a deliberate
 *  choice.
 *
 *  `scale` holds the text the user typed in the scale box. The editor reads
 *  it when the drawer is saved and writes a transform only when the factor
 *  is not 1, so a widget nobody scaled is saved exactly as it was before
 *  scaling existed. */
export interface MultipleTextValuesEntryDraft {
  id: string;
  machine: MachineNameAndSourceId | null;
  node: MachineNameAndSourceId | null;
  label: string;
  shouldAutoFillLabel: boolean;
  unit: string | null;
  scale: string;
}

interface MultipleTextValuesEntryCardProps {
  entry: MultipleTextValuesEntryDraft;
  /** 1-based position, used in the field labels ("Entry 2"). */
  index: number;
  machineOptions: MachineNameAndSourceId[];
  nodeOptions: MachineNameAndSourceId[];
  unitOptions: MachineNameAndSourceId[];
  isLoadingMachines: boolean;
  isLoadingNodes: boolean;
  isLoadingUnits: boolean;
  /** False on the last remaining entry — the × is hidden, not greyed. */
  removable: boolean;
  onMachineChange: (machine: MachineNameAndSourceId | null) => void;
  onNodeChange: (node: MachineNameAndSourceId | null) => void;
  onLabelChange: (label: string) => void;
  onUnitChange: (unit: MachineNameAndSourceId | null) => void;
  onScaleChange: (scale: string) => void;
  onRemove: () => void;
  texts: {
    entryLabel: string;
    entryLabelPlaceholder: string;
    machineLabel: string;
    noMachines: string;
    machinesPlaceholder: string;
    nodeLabel: string;
    noNodes: string;
    nodesPlaceholder: string;
    unitLabel: string;
    noUnits: string;
    unitsPlaceholder: string;
    removeEntry: string;
  };
}

export const MultipleTextValuesEntryCard = ({
  entry,
  index,
  machineOptions,
  nodeOptions,
  unitOptions,
  isLoadingMachines,
  isLoadingNodes,
  isLoadingUnits,
  removable,
  onMachineChange,
  onNodeChange,
  onLabelChange,
  onUnitChange,
  onScaleChange,
  onRemove,
  texts,
}: MultipleTextValuesEntryCardProps) => {
  const withIndex = (template: string): string =>
    template.replace("{{ index }}", String(index));

  return (
    <div className={styles[cssClassName]}>
      <DashboardEditWidgetDrawerDropdown
        options={machineOptions}
        selectedOption={entry.machine}
        onSelectOption={onMachineChange}
        labelText={texts.machineLabel}
        noOptionsText={texts.noMachines}
        placeholderText={texts.machinesPlaceholder}
        isLoading={isLoadingMachines}
      />
      <DashboardEditWidgetDrawerDropdown
        options={nodeOptions}
        selectedOption={entry.node}
        onSelectOption={onNodeChange}
        labelText={withIndex(texts.nodeLabel)}
        noOptionsText={texts.noNodes}
        placeholderText={texts.nodesPlaceholder}
        isLoading={isLoadingNodes}
      />
      <DashboardEditWidgetDrawerDropdown
        options={unitOptions}
        selectedOption={
          entry.unit != null
            ? unitOptions.find((option) => option.source_id === entry.unit) ??
              null
            : null
        }
        onSelectOption={onUnitChange}
        labelText={withIndex(texts.unitLabel)}
        noOptionsText={texts.noUnits}
        placeholderText={texts.unitsPlaceholder}
        isLoading={isLoadingUnits}
      />
      {/* The scale box sits right after the unit, next to the field it
          scales. Its label and placeholder come from the shared scale-input
          atom, so this editor reads the same as single-text-value. */}
      <ScaleInput value={entry.scale} onChange={onScaleChange} />
      {/* Label last: it auto-fills from the picked node, so it is the field
          the user touches least. */}
      <Input2025
        inputPlaceHolder={withIndex(texts.entryLabelPlaceholder)}
        labelText={withIndex(texts.entryLabel)}
        inputValue={entry.label}
        onChangeFunc={(e) => onLabelChange(e.target.value)}
        inputVariant="dark-background"
      />
      {removable && (
        <button
          type="button"
          className={styles[`${cssClassName}__remove`]}
          onClick={onRemove}
          aria-label={withIndex(texts.removeEntry)}
        >
          <img src={closeIcon} alt="" />
        </button>
      )}
    </div>
  );
};
